import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, ImageIcon, X, Plus, Save, Eye, Loader2, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import MediaSelectorModal from "../library/media-selector";
import { normalizeBlogForDetail } from "@/utils/helper/blog";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getBlogById,
  updateBlog,
  selectCurrentBlog,
  selectCurrentBlogLoading,
  selectUpdatingBlog,
  clearCurrentBlog,
} from "@/store/slices/blogSlice";
import RichTextEditor from "@/components/ui/rich-text-editor";
import Loader from "@/components/loader/loader";

export default function EditBlog() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const blogData = useAppSelector(selectCurrentBlog);
  const loading = useAppSelector(selectCurrentBlogLoading);
  const isSubmitting = useAppSelector(selectUpdatingBlog);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    tags: [],
    featuredImage: null,
    status: "draft",
    publishDate: new Date(),
    metaTitle: "",
    metaDescription: "",
    scheduleDate: null,
  });

  const [newTag, setNewTag] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [blogId, setBlogId] = useState(null);

  const categories = [
    "Skincare Tips",
    "Product Reviews",
    "Ingredients Guide",
    "Beauty Trends",
    "Wellness",
    "How-to Guides",
  ];

  // Fetch blog data on mount
  useEffect(() => {
    if (slug) {
      dispatch(getBlogById(slug));
    }
    return () => {
      dispatch(clearCurrentBlog());
    }
  }, [slug, dispatch]);

  // Populate form when blog data is loaded
  useEffect(() => {
    if (blogData && blogData.id) {
      // Normalize blog data to ensure consistent structure
      const normalizedBlog = normalizeBlogForDetail(blogData);
      
      // Parse categories from the blog data
      let blogCategories = [];
      try {
        // Try to get categories from the blog data if available
        if (blogData.categories) {
          blogCategories = typeof blogData.categories === 'string' 
            ? JSON.parse(blogData.categories) 
            : (Array.isArray(blogData.categories) ? blogData.categories : []);
        }
      } catch (e) {
        blogCategories = [];
      }

      // Parse tags
      const blogTags = normalizedBlog.tags || [];

      // Get featured image - check multiple sources
      let featuredImageUrl = null;
      
      // First, try to get from normalized blog data
      if (normalizedBlog.featuredImage && 
          normalizedBlog.featuredImage !== "/placeholder.svg" &&
          (normalizedBlog.featuredImage.startsWith('http://') || 
           normalizedBlog.featuredImage.startsWith('https://') ||
           normalizedBlog.featuredImage.startsWith('/'))) {
        featuredImageUrl = normalizedBlog.featuredImage;
      }
      
      // Fallback: check raw blogData.images array (backend returns array of strings)
      if (!featuredImageUrl && blogData.images && Array.isArray(blogData.images) && blogData.images.length > 0) {
        const firstImage = blogData.images[0];
        // Handle string URLs directly
        if (typeof firstImage === 'string' && firstImage.trim() && firstImage !== "/placeholder.svg") {
          featuredImageUrl = firstImage.trim();
        }
        // Handle object with image property
        else if (typeof firstImage === 'object' && firstImage !== null) {
          if (firstImage.image && typeof firstImage.image === 'string' && firstImage.image !== "/placeholder.svg") {
            featuredImageUrl = firstImage.image.trim();
          } else if (firstImage.url && typeof firstImage.url === 'string' && firstImage.url !== "/placeholder.svg") {
            featuredImageUrl = firstImage.url.trim();
          }
        }
      }
      
      // Final validation - ensure it's a valid URL
      if (featuredImageUrl && 
          featuredImageUrl !== "/placeholder.svg" &&
          !featuredImageUrl.startsWith('http://') && 
          !featuredImageUrl.startsWith('https://') && 
          !featuredImageUrl.startsWith('/')) {
        featuredImageUrl = null;
      }

      // Parse scheduleDate if it exists
      let scheduleDate = null;
      if (blogData.scheduleDate) {
        try {
          scheduleDate = new Date(blogData.scheduleDate);
        } catch (e) {
          scheduleDate = null;
        }
      }

      // Determine which date to use for publishDate field
      let publishDate = new Date();
      if (blogData.status === "scheduled" && scheduleDate) {
        publishDate = scheduleDate;
      } else if (normalizedBlog.publishDate) {
        publishDate = new Date(normalizedBlog.publishDate);
      }

      // Set form data
      setFormData({
        title: normalizedBlog.title || "",
        excerpt: normalizedBlog.excerpt || "",
        content: normalizedBlog.content || "",
        category: blogCategories[0] || normalizedBlog.category || "",
        tags: blogTags || [],
        featuredImage: featuredImageUrl,
        status: blogData.status || "draft",
        publishDate: publishDate,
        metaTitle: normalizedBlog.metaTitle || normalizedBlog.title || "",
        metaDescription: normalizedBlog.metaDescription || normalizedBlog.excerpt || "",
        scheduleDate: scheduleDate || publishDate,
      });

      setBlogId(normalizedBlog.id || null);
      setImagePreview(featuredImageUrl);
      setSelectedImage(featuredImageUrl);
    }
  }, [blogData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (status) => {
    // Validate required fields
    if (!formData.title?.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.content?.trim()) {
      toast.error("Content is required");
      return;
    }

    try {
      // Prepare blog data according to backend API expectations
      const updateData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        metaTitle: formData.metaTitle?.trim() || formData.title.trim(),
        metaDescription: formData.metaDescription?.trim() || formData.excerpt?.trim() || "",
        status: status,
        categories: formData.category ? [formData.category] : [],
        tags: formData.tags || [],
        images: selectedImage ? [selectedImage] : [],
      };

      // Handle scheduleDate based on status
      if (status === "scheduled") {
        // Use scheduleDate if it exists, otherwise use publishDate, or set a default future date
        const dateToUse = formData.scheduleDate || formData.publishDate;
        if (dateToUse) {
          updateData.scheduleDate = dateToUse.toISOString();
        } else {
          // If no date is set and status is scheduled, set it to 7 days from now
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + 7);
          updateData.scheduleDate = futureDate.toISOString();
        }
      }

      // Use blogId if available, otherwise use slug
      const identifier = blogId || slug;
      await dispatch(updateBlog({ blogId: identifier, blogData: updateData })).unwrap();

      // Navigate to blog list after a short delay
      setTimeout(() => {
        navigate("/admin/blog/list");
      }, 1500);
    } catch (error) {
      console.error("Error updating blog:", error);
      // Error already handled in thunk
    }
  };

  const handleMediaSelect = (file) => {
    if (file && file.type === "image") {
      // Extract Cloudinary URL from file object
      const imageUrl = file.url || file.path || file.image;
      
      // Validate that it's a valid URL (Cloudinary URLs start with http)
      if (imageUrl && (imageUrl.startsWith('http') || imageUrl.startsWith('/'))) {
        setImagePreview(imageUrl);
        setSelectedImage(imageUrl);
        setFormData((prev) => ({
          ...prev,
          featuredImage: imageUrl,
        }));
        setIsMediaModalOpen(false);
      } else {
        toast.error("Invalid image URL. Please select a valid image.");
      }
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      featuredImage: null,
    }));
    setImagePreview(null);
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6 flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/blog/list">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Edit Blog Post</h1>
            <p className="text-muted-foreground">
              Update your blog post content and settings
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={isSubmitting}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSubmit(formData.status)}
            disabled={isSubmitting}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
          <Button
            onClick={() => handleSubmit("published")}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update & Publish"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Enter the main details for your blog post
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Enter blog post title"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange("excerpt", e.target.value)}
                  placeholder="Brief description of the blog post"
                  className="mt-1"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card>
            <CardHeader>
              <CardTitle>Content</CardTitle>
              <CardDescription>Write your blog post content</CardDescription>
            </CardHeader>
            <CardContent>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => handleInputChange("content", value)}
                placeholder="Write your blog post content here..."
                height="400px"
              />
              <p className="text-sm text-muted-foreground mt-2">
                {formData.content?.replace(/<[^>]*>/g, '').length || 0} characters (excluding HTML)
              </p>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Settings</CardTitle>
              <CardDescription>
                Optimize your blog post for search engines
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="metaTitle">Meta Title</Label>
                <Input
                  id="metaTitle"
                  value={formData.metaTitle}
                  onChange={(e) =>
                    handleInputChange("metaTitle", e.target.value)
                  }
                  placeholder="SEO title (leave empty to use post title)"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="metaDescription">Meta Description</Label>
                <Textarea
                  id="metaDescription"
                  value={formData.metaDescription}
                  onChange={(e) =>
                    handleInputChange("metaDescription", e.target.value)
                  }
                  placeholder="SEO description for search results"
                  className="mt-1"
                  rows={3}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.metaDescription.length}/160 characters
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publish Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Publish Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleInputChange("status", value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Publish Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full mt-1 justify-start text-left font-normal bg-transparent"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.publishDate
                        ? format(formData.publishDate, "PPP")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.publishDate}
                      onSelect={(date) =>
                        handleInputChange("publishDate", date)
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Category & Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Category & Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    handleInputChange("category", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addTag())
                    }
                  />
                  <Button type="button" size="sm" onClick={addTag}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="flex items-center gap-1.5 pr-1"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeTag(tag);
                        }}
                        className="ml-1 rounded-full hover:bg-destructive/20 p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label={`Remove tag ${tag}`}
                      >
                        <X className="w-3 h-3 cursor-pointer hover:text-destructive" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Featured Image */}
          <Card>
            <CardHeader>
              <CardTitle>Featured Image</CardTitle>
            </CardHeader>
            <CardContent>
              {imagePreview && imagePreview !== "/placeholder.svg" && imagePreview.trim() !== "" ? (
                <div className="space-y-2">
                  <div className="relative group">
                    <img
                      src={imagePreview}
                      alt="Featured image preview"
                      className="w-full h-40 object-cover rounded-md border"
                      loading="lazy"
                      onError={(e) => {
                        // Prevent infinite loop - if image fails, remove it from state
                        console.error("Failed to load image:", imagePreview);
                        removeImage();
                      }}
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={removeImage}
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {selectedImage && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground truncate" title={selectedImage}>
                        Image URL: {selectedImage.length > 50 ? selectedImage.substring(0, 50) + '...' : selectedImage}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        ✓ Image loaded successfully
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <Button
                      variant="secondary"
                      onClick={() => setIsMediaModalOpen(true)}
                    >
                      Choose from Media Library
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Media Selector Modal */}
          <MediaSelectorModal
            isOpen={isMediaModalOpen}
            onClose={() => setIsMediaModalOpen(false)}
            onSelect={handleMediaSelect}
            multiple={false}
            fileTypes={["image"]}
            title="Select Featured Image"
            description="Choose an image from your media library"
          />
        </div>
      </div>
    </div>
  );
}

